import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';

import type { RootState } from '@/redux/store';
import useFeaturesReady from '@/shared/featureFlags/useFeaturesReady';
import type { UserInvoicingRegime } from '@/shared/services/invoice.service';

import { INVOICE_TESTING_FLAG } from '../../constants/flag';
import { regimeFitsRfc } from '../../constants/satCatalogs';
import type { CsdSeal } from '../../services/csdSeal.service';
import { useInvoicingData } from './useInvoicingData';
import { useSealStatus, type SealStatus } from './useSealStatus';
import {
  activeRetentionRates,
  draftTotals,
  isValidPostalCode,
  missingRequiredFields,
  retentionScope,
  ruleRatesOf,
  waivedRetentionsOf,
  withRetentions,
  type DraftIssuer,
  type InvoiceDraft,
  type RetentionRates,
  type RetentionTax,
} from '../lib/draft';
import { useInvoiceDraft, type SaveState } from './useInvoiceDraft';
import { useRetentionRule } from './useRetentionRule';

interface InvoiceFlowValue {
  draft: InvoiceDraft;
  update: ReturnType<typeof useInvoiceDraft>['update'];
  reset: () => void;
  replace: ReturnType<typeof useInvoiceDraft>['replace'];
  saveState: SaveState;
  issuer: DraftIssuer | null;
  /** Los regímenes con los que el emisor puede timbrar. */
  issuerRegimes: UserInvoicingRegime[];
  /** Con más de un régimen, el bloque del emisor de conceptos y la revisión dejan elegirlo. */
  canChooseIssuerRegime: boolean;
  chooseIssuerRegime: (code: string) => void;
  /** El CP del perfil fiscal, el de la constancia más reciente: el que se propone sin elección. */
  issuerProfilePostalCode: string;
  /** El lugar de expedición, para esta factura y las siguientes de este RFC en este dispositivo. */
  chooseIssuerPostalCode: (postalCode: string) => void;
  hasStamps: boolean;
  /** El sello registrado. Es la fuente del RFC emisor al timbrar, nunca un campo del formulario. */
  seal: CsdSeal | null;
  isStampStatusLoading: boolean;
  /** Sabemos que no hay sello vigente: ni cargando ni con la consulta caída. Lo único que manda a subir sellos. */
  stampsMissing: boolean;
  /** Si la consulta del sello falló sin respuesta: se ofrece reintentar, nunca subir otro. */
  stampCheck: Pick<SealStatus, 'checkFailed' | 'isRechecking' | 'recheck' | 'pendingActivation'>;
  totals: ReturnType<typeof draftTotals>;
  missingFields: string[];
  /** Al retomar este borrador, su fecha retroactiva ya había caducado y regresó a hoy. */
  backdateWasReset: boolean;
  /** Por qué no se puede timbrar todavía por las retenciones, o `null`. Nunca frena la navegación entre pasos. */
  retentionsBlock: string | null;
  /** La regla de este emisor con este receptor todavía no llega. */
  retentionsResolving: boolean;
  /** La tasa que pone la regla por impuesto, o `null` si no lo retiene. El default del editor. */
  ruleRates: Record<RetentionTax, number | null>;
  /** Las tasas que capturó el usuario para este emisor y este receptor (ver `RetentionRates`). */
  capturedRates: Pick<RetentionRates, RetentionTax>;
  /** Las retenciones de la regla que el usuario quitó («Mi cliente no me retiene»), o `null`. */
  retentionsWaived: Record<RetentionTax, boolean> | null;
  /** Quita o devuelve una retención de la regla, solo para este emisor y este receptor. */
  setRetentionWaived: (tax: RetentionTax, waived: boolean) => void;
  /**
   * Captura la tasa de un impuesto, solo para este emisor y este receptor. `undefined`
   * regresa a la regla (o a no retenerlo, si no hay regla); `null` es «falta la tasa».
   */
  setRetentionRate: (tax: RetentionTax, rate: number | null | undefined) => void;
  /**
   * Si la UI ofrece elegir entre prueba y real. Misma bandera que el «Modo
   * testing» del flujo legacy; sin ella la emisión es siempre real.
   */
  canChooseEmissionMode: boolean;
  /**
   * Si la emisión va por `/cfdi/emit/test`. Solo quien tiene la bandera puede
   * timbrar en prueba, y para esa persona es el default. Vive aquí y no en una
   * vista porque se elige en el inicio o en revisar y «Subir sellos y timbrar»
   * sale de otra ruta: el modo tiene que sobrevivir esos brincos. No se
   * persiste: un refresh vuelve al default, y `reset()` también.
   */
  isTestEmission: boolean;
  setTestEmission: (isTest: boolean) => void;
}

const InvoiceFlowContext = createContext<InvoiceFlowValue | null>(null);

const ISSUER_REGIME_STORAGE_KEY = 'heru_invoice_v2_issuer_regime';
/** Sin actividad propia que facturar: solo van por default si el emisor no tiene otro. */
const NON_DEFAULT_ISSUER_REGIMES = new Set(['605', '616']);

const readStoredIssuerRegime = (): string | null => {
  try {
    return localStorage.getItem(ISSUER_REGIME_STORAGE_KEY);
  } catch {
    return null;
  }
};

const ISSUER_POSTAL_CODE_STORAGE_KEY = 'heru_invoice_v2_issuer_postal_code';

/**
 * El CP que eligió el usuario, con el RFC del emisor que lo eligió: el storage es
 * del navegador, no de la cuenta, y sin esa marca otra sesión en el mismo equipo
 * timbraría con el lugar de expedición de alguien más.
 */
interface StoredIssuerPostalCode {
  rfc: string;
  postalCode: string;
}

const readStoredIssuerPostalCode = (): StoredIssuerPostalCode | null => {
  try {
    const raw = localStorage.getItem(ISSUER_POSTAL_CODE_STORAGE_KEY);
    const stored = raw ? (JSON.parse(raw) as Partial<StoredIssuerPostalCode>) : null;
    return stored?.rfc && isValidPostalCode(stored.postalCode)
      ? { rfc: stored.rfc, postalCode: stored.postalCode as string }
      : null;
  } catch {
    return null;
  }
};

const writeStoredIssuerPostalCode = (stored: StoredIssuerPostalCode | null) => {
  try {
    if (stored) localStorage.setItem(ISSUER_POSTAL_CODE_STORAGE_KEY, JSON.stringify(stored));
    else localStorage.removeItem(ISSUER_POSTAL_CODE_STORAGE_KEY);
  } catch {
    // Sin storage, la elección dura lo que la pestaña.
  }
};

const sameRfc = (a: string, b: string) => a.trim().toUpperCase() === b.trim().toUpperCase();

const isActiveRegime = (regime: { end_date: string | null }) =>
  !regime.end_date || new Date(regime.end_date).getTime() > Date.now();

const fittingRegimes = (regimes: UserInvoicingRegime[], rfc: string) =>
  regimes.filter(
    (regime, index) =>
      !!regime.code &&
      regimeFitsRfc(regime.code, rfc) &&
      regimes.findIndex((other) => other.code === regime.code) === index,
  );

/**
 * El borrador vive por encima de las tres rutas de paso, así que ir y volver
 * entre pasos no pasa por storage y no puede perder la última edición contra
 * el debounce del autoguardado.
 */
export const InvoiceFlowProvider = ({ children }: { children: ReactNode }) => {
  const { data: invoicingData } = useInvoicingData();
  const seals = useSealStatus();

  const taxpayer = useSelector((state: RootState) => state.taxProfile.taxpayer);
  const accountEmail = useSelector((state: RootState) => state.profile.user?.email);
  const issuerRfc = invoicingData?.rfc || seals.seal?.rfc || taxpayer?.rfc || '';

  /**
   * Los regímenes activos del perfil fiscal son los del SAT. Sin perfil (sin
   * vínculo activo) quedan los de `/operations/user`, que cuando no conoce los
   * del usuario manda una lista genérica que empieza por 601: por eso se
   * descartan los que no son de su tipo de persona.
   */
  const { issuerRegimes, regimesFromProfile } = useMemo(() => {
    const profileRegimes = (taxpayer?.tax_status?.regimes ?? [])
      .filter(isActiveRegime)
      .map(({ code, name }) => ({ code, name }));
    const fromProfile = fittingRegimes(profileRegimes, issuerRfc);
    return fromProfile.length > 0
      ? { issuerRegimes: fromProfile, regimesFromProfile: true }
      : {
          issuerRegimes: fittingRegimes(invoicingData?.regimes ?? [], issuerRfc),
          regimesFromProfile: false,
        };
  }, [taxpayer?.tax_status?.regimes, invoicingData?.regimes, issuerRfc]);

  const [chosenRegime, setChosenRegime] = useState(readStoredIssuerRegime);
  const chooseIssuerRegime = useCallback((code: string) => {
    setChosenRegime(code);
    try {
      localStorage.setItem(ISSUER_REGIME_STORAGE_KEY, code);
    } catch {
      // Sin storage, la elección dura lo que la pestaña.
    }
  }, []);
  /**
   * Without a fiscal profile (an account that never linked the SAT, like one
   * that registered its seals straight from the onboarding) the list is the
   * generic one of `/operations/user`, not the person's regimes. Preselecting
   * its first entry would stamp every invoice under a regime nobody chose, so
   * among several nothing is preselected: the person picks it once and the
   * choice is remembered. A single fitting option is still taken.
   */
  const preselectRegime = regimesFromProfile || issuerRegimes.length === 1;
  const issuerRegime =
    issuerRegimes.find((regime) => regime.code === chosenRegime) ??
    (preselectRegime
      ? (issuerRegimes.find((regime) => !NON_DEFAULT_ISSUER_REGIMES.has(regime.code)) ??
        issuerRegimes[0])
      : undefined);

  /**
   * El CP que se propone sale de tres fuentes porque ninguna contesta siempre.
   *
   * `/operations/user` da el de la constancia más reciente, sin pedir vínculo con
   * el SAT, pero puede no contestar. El perfil fiscal (`/taxpayer/info`, ya
   * cargado por el bootstrap) trae el domicilio más reciente. El sello va al
   * último: su `postal_code` es una copia que nadie actualiza.
   */
  const issuerProfilePostalCode =
    invoicingData?.postalCode ||
    taxpayer?.address?.postal_code ||
    taxpayer?.credentials?.certificates?.postal_code ||
    '';

  /**
   * El lugar de expedición es el CP desde donde se factura, no por fuerza el de
   * la constancia: una sucursal, o una constancia que todavía no se actualiza.
   * Por eso el usuario lo puede cambiar, y se recuerda como el régimen.
   */
  const [storedPostalCode, setStoredPostalCode] = useState(readStoredIssuerPostalCode);
  const chosenPostalCode =
    storedPostalCode && sameRfc(storedPostalCode.rfc, issuerRfc) ? storedPostalCode.postalCode : null;
  const chooseIssuerPostalCode = useCallback(
    (postalCode: string) => {
      // Volver al de la constancia borra la elección: si la constancia cambia después, se sigue sola.
      const next = postalCode === issuerProfilePostalCode ? null : { rfc: issuerRfc, postalCode };
      setStoredPostalCode(next);
      writeStoredIssuerPostalCode(next);
    },
    [issuerRfc, issuerProfilePostalCode],
  );

  const issuer = useMemo<DraftIssuer | null>(() => {
    if (!issuerRfc) return null;

    return {
      name: invoicingData?.name || seals.seal?.name || taxpayer?.name || '',
      rfc: issuerRfc,
      taxRegime: issuerRegime?.code ?? '',
      taxRegimeName: issuerRegime?.shortName ?? issuerRegime?.name,
      postalCode: chosenPostalCode ?? issuerProfilePostalCode,
      email: accountEmail,
    };
  }, [
    issuerRfc,
    issuerRegime,
    invoicingData,
    seals.seal,
    taxpayer,
    chosenPostalCode,
    issuerProfilePostalCode,
    accountEmail,
  ]);

  const {
    draft: capturedDraft,
    update,
    reset: resetDraft,
    replace,
    saveState,
    backdateResetDraftId,
  } = useInvoiceDraft(issuer);
  const retention = useRetentionRule(capturedDraft.issuer, capturedDraft.receiver);
  // Lo que ven las pantallas y lo que se timbra: el borrador con las retenciones del régimen.
  const draft = useMemo(
    () => withRetentions(capturedDraft, retention.rule),
    [capturedDraft, retention.rule],
  );
  const ruleRates = useMemo(
    () => ruleRatesOf(capturedDraft, retention.rule),
    [capturedDraft, retention.rule],
  );
  const capturedRates = useMemo(() => {
    const active = activeRetentionRates(capturedDraft);
    return { isr: active?.isr, iva: active?.iva };
  }, [capturedDraft]);
  const retentionsWaived = useMemo(
    () => waivedRetentionsOf(capturedDraft, retention.rule),
    [capturedDraft, retention.rule],
  );
  const setRetentionRate = useCallback(
    (tax: RetentionTax, rate: number | null | undefined) =>
      update((current) => {
        const scope = retentionScope(current);
        // Unas tasas de otro receptor o de otro régimen ya no aplican: se parte de cero.
        const previous = current.retentionRates?.scope === scope ? current.retentionRates : undefined;
        const next: RetentionRates = { isr: previous?.isr, iva: previous?.iva, scope, [tax]: rate };
        return {
          retentionRates: next.isr !== undefined || next.iva !== undefined ? next : undefined,
        };
      }),
    [update],
  );
  const setRetentionWaived = useCallback(
    (tax: RetentionTax, waived: boolean) => {
      // Quitarla también borra la tasa capturada: al devolverla regresa la de la regla.
      if (waived) setRetentionRate(tax, undefined);
      update((current) => {
        const scope = retentionScope(current);
        // Una excepción de otro receptor o de otro régimen ya no aplica: se parte de cero.
        const previous =
          current.retentionWaiver?.scope === scope ? current.retentionWaiver : undefined;
        const next = { isr: previous?.isr ?? false, iva: previous?.iva ?? false, scope, [tax]: waived };
        return { retentionWaiver: next.isr || next.iva ? next : undefined };
      });
    },
    [update, setRetentionRate],
  );
  const retentionsBlock = retention.isResolving
    ? 'Estamos calculando las retenciones de este receptor.'
    : retention.failed
      ? 'No pudimos calcular las retenciones de este receptor. Recarga la página para intentarlo de nuevo.'
      : null;

  const featuresReady = useFeaturesReady();
  const testingToggleIsOn = useFeatureIsOn(INVOICE_TESTING_FLAG);
  const canChooseEmissionMode = featuresReady && testingToggleIsOn;

  // Con la bandera, real se ARMA para la emisión en curso y se desarma al
  // terminar o descartar: el borrador siguiente vuelve a empezar en prueba.
  const [realEmission, setRealEmission] = useState(false);
  const isTestEmission = canChooseEmissionMode && !realEmission;
  const setTestEmission = useCallback((isTest: boolean) => setRealEmission(!isTest), []);
  const reset = useCallback(() => {
    setRealEmission(false);
    resetDraft();
  }, [resetDraft]);

  const value = useMemo<InvoiceFlowValue>(
    () => ({
      draft,
      update,
      reset,
      replace,
      saveState,
      issuer,
      issuerRegimes,
      canChooseIssuerRegime: issuerRegimes.length > 1,
      chooseIssuerRegime,
      issuerProfilePostalCode,
      chooseIssuerPostalCode,
      hasStamps: seals.hasSeal,
      seal: seals.seal,
      isStampStatusLoading: seals.isLoading,
      stampsMissing: !seals.hasSeal && !seals.isLoading && !seals.checkFailed,
      stampCheck: seals,
      totals: draftTotals(draft),
      missingFields: missingRequiredFields(draft),
      backdateWasReset: backdateResetDraftId === draft.id && !draft.backdatedTo,
      retentionsBlock,
      retentionsResolving: retention.isResolving,
      ruleRates,
      capturedRates,
      retentionsWaived,
      setRetentionWaived,
      setRetentionRate,
      canChooseEmissionMode,
      isTestEmission,
      setTestEmission,
    }),
    [
      draft,
      update,
      reset,
      replace,
      saveState,
      issuer,
      issuerRegimes,
      chooseIssuerRegime,
      issuerProfilePostalCode,
      chooseIssuerPostalCode,
      seals,
      backdateResetDraftId,
      retentionsBlock,
      retention.isResolving,
      ruleRates,
      capturedRates,
      retentionsWaived,
      setRetentionWaived,
      setRetentionRate,
      canChooseEmissionMode,
      isTestEmission,
      setTestEmission,
    ],
  );

  return <InvoiceFlowContext.Provider value={value}>{children}</InvoiceFlowContext.Provider>;
};

export const useInvoiceFlow = (): InvoiceFlowValue => {
  const context = useContext(InvoiceFlowContext);
  if (!context) throw new Error('useInvoiceFlow debe usarse dentro de InvoiceFlowProvider');
  return context;
};
